import React, { useState, useEffect } from 'react';
import { Badge, Table, Button, Collapse, Spinner, Form } from 'react-bootstrap';
import { Package, Code, ChevronDown, Upload, ExternalLink } from 'lucide-react';
import type { RestructuredProduct } from '../../../../../infrastructure/SourcingClient';
import type { WpProductPayload } from '../../../../../models/WpProduct';
import { useUploadMultipleToWordPress } from '../../../../../apps/hooks/product/useProduct';
import { useAuth } from '../../../../../providers/AuthProvider';
import { getConfig } from '../../../../../config';
import { StatusBadge } from '../../../../../components/ui/badge/StatusBadge';

interface WpUploadPreviewProps {
  products: RestructuredProduct[];
  matchedProduct: any;
  cmWorkflowData?: any;
  refetchCMWorkflow?: () => Promise<any>;
  refetchMatchedProduct?: () => Promise<any>;
  refetchProductDetails?: (forceRefresh?: boolean) => Promise<any>;
}

export const WpUploadPreview: React.FC<WpUploadPreviewProps> = ({
  products,
  matchedProduct,
  cmWorkflowData,
  refetchCMWorkflow,
  refetchMatchedProduct,
  refetchProductDetails,
}) => {
  const { user } = useAuth();
  const [showAllFields, setShowAllFields] = useState(false);
  const [showJsonPreview, setShowJsonPreview] = useState<number | null>(null);
  const [editedCells, setEditedCells] = useState<Map<string, any>>(new Map());
  const [uploadedSkus, setUploadedSkus] = useState<Map<number, string>>(new Map());
  const [fetchedProductStatuses, setFetchedProductStatuses] = useState<Map<string, any>>(new Map());

  const uploadMutation = useUploadMultipleToWordPress();

  // Create a mapping from cpii (cpItemId) to SKU from CM Workflow data
  const cpiiToSkuMap = new Map<string, string>();
  if (cmWorkflowData && cmWorkflowData.length > 0) {
    const skuMappings = cmWorkflowData[0]?.skuCpiiMappings;
    if (skuMappings) {
      skuMappings.forEach((mapping: any) => {
        cpiiToSkuMap.set(mapping.cpii, mapping.sku);
      });
    }
  }

  // Find the variable product to inherit size and color from
  const variableProduct = products.find((p) => p.type === 'variable');
  const inheritedSize = variableProduct?.attributeSize;
  const inheritedColor = variableProduct?.attributeColor;

  const transformToWpPayload = (product: RestructuredProduct): WpProductPayload => {
    const glowpickProduct = matchedProduct.gpDetailedProduct;
    const coupangProduct = matchedProduct.cpDetailedProduct;
    const scAgentEngData = glowpickProduct?.scAgentEngData;

    // Data Sources:
    // - Basic product info (brand, categories, description, tags, etc.) from scAgentEngData
    // - Attributes (size, pack, color) from restructured product data (product.attributeSize, etc.)
    // - Meta data from multiple sources (GP, CP, scAgentEngData)

    // Get SKU from CM workflow mappings (if exists)
    const existingSku = product.cpItemId ? cpiiToSkuMap.get(product.cpItemId) : undefined;

    // Use categories from scAgentEngData
    const categories = scAgentEngData?.categories || '';

    // Build meta_data array (exact API format)
    const metaData: Array<{ key: string; value: string }> = [];

    if (matchedProduct.gpProductId) {
      metaData.push({ key: 'gp_product_id', value: matchedProduct.gpProductId });
    }
    if (matchedProduct.cpProductId) {
      metaData.push({ key: 'cp_parent_code', value: matchedProduct.cpProductId });
    }
    if (product.cpItemId) {
      metaData.push({ key: 'cp_child_code', value: product.cpItemId });
    }
    if (product.cpItemName) {
      metaData.push({ key: 'cp_item_name', value: product.cpItemName });
    }
    if (scAgentEngData?.name) {
      metaData.push({ key: 'ss_name_only', value: scAgentEngData.name });
    }
    if (glowpickProduct?.name) {
      metaData.push({ key: 'gp_name_kr', value: glowpickProduct.name });
    }
    if (glowpickProduct?.brand) {
      metaData.push({ key: 'gp_brand_kr', value: glowpickProduct.brand });
    }
    if (glowpickProduct?.benefits) {
      metaData.push({ key: 'gp_benefit_kr', value: glowpickProduct.benefits });
    }
    if (glowpickProduct?.tags && Array.isArray(glowpickProduct.tags)) {
      metaData.push({ key: 'gp_tag_kr', value: glowpickProduct.tags.join(', ') });
    }
    if (coupangProduct?.name) {
      // Remove ",1개" from the end if it exists
      const cpNameKr = coupangProduct.name.replace(/,\s*1개$/, '');
      metaData.push({ key: 'cp_name_kr', value: cpNameKr });
    }
    if (glowpickProduct?.name) {
      metaData.push({ key: 'ss_name_kor', value: glowpickProduct.name });
    }
    if (glowpickProduct?.scAgentEngData?.name && glowpickProduct?.scAgentEngData?.brand) {
      metaData.push({
        key: 'title_with_brand',
        value: glowpickProduct.scAgentEngData.brand + ' ' + glowpickProduct.scAgentEngData.name,
      });
    }
    if (product.cpUrl) {
      metaData.push({ key: 'cp_url', value: product.cpUrl });
    }
    if (coupangProduct?.reviews) {
      metaData.push({ key: 'cp_review_count', value: String(coupangProduct.reviews) });
    }
    if (coupangProduct?.rating) {
      metaData.push({ key: 'star', value: String(coupangProduct.rating) });
    }
    if (product.cpListPrice) {
      metaData.push({ key: 'cp_list_price', value: product.cpListPrice });
    }
    // Add cp_discount_price (only for variation and simple, not variable)
    if (coupangProduct?.priceInfo?.salePrice && product.type !== 'variable') {
      metaData.push({ key: 'cp_discount_price', value: coupangProduct.priceInfo.salePrice });
    }
    // Add wave from CM workflow data
    const wave = cmWorkflowData?.[0]?.wave;
    if (wave) {
      metaData.push({ key: 'wave', value: wave });
    }
    if (scAgentEngData?.keyBenefits) {
      metaData.push({ key: 'key_benefits', value: scAgentEngData.keyBenefits });
    }
    if (scAgentEngData?.keyIngredients) {
      metaData.push({ key: 'key_ingredients', value: scAgentEngData.keyIngredients });
    }
    if (scAgentEngData?.keyIngredientTags) {
      metaData.push({ key: 'key_ingredient_tags', value: scAgentEngData.keyIngredientTags });
    }
    if (scAgentEngData?.efficacyTags) {
      metaData.push({ key: 'efficacy_tags', value: scAgentEngData.efficacyTags });
    }
    if (scAgentEngData?.skinConcernTags) {
      metaData.push({ key: 'skin_concern_tags', value: scAgentEngData.skinConcernTags });
    }
    if (scAgentEngData?.skinTypeTags) {
      metaData.push({ key: 'skin_type_tags', value: scAgentEngData.skinTypeTags });
    }
    // Add tags to meta_data
    if (scAgentEngData?.tags) {
      metaData.push({ key: 'tags', value: scAgentEngData.tags });
    }
    // Add highlight to meta_data
    if (scAgentEngData?.description) {
      metaData.push({ key: 'highlight', value: scAgentEngData.description });
    }

    // Build attributes array from restructured product data (CpGpMatchedProduct.ssRestructuredProducts)
    const attributes: Array<{ name: string; options: string[] } | { name: string; option: string }> = [];

    if (product.type === 'variable') {
      // Variable product: collect all unique options from Variation products only (not from Variable itself)
      const sizeOptions = new Set<string>();
      const packOptions = new Set<string>();
      const colorOptions = new Set<string>();

      products.forEach((p) => {
        // Only collect from variation products, not from variable products
        if (p.type === 'variation') {
          // Size from variation (should inherit from variable product)
          const variationSize = p.attributeSize || inheritedSize;
          if (variationSize) {
            sizeOptions.add(variationSize);
          }
          if (p.attributePack) {
            packOptions.add(p.attributePack);
          }
          // Color from variation (should inherit from variable product)
          const variationColor = p.attributeColor || inheritedColor;
          if (variationColor) {
            colorOptions.add(variationColor);
          }
        }
      });

      // If no variations found, use the variable product's own attributes
      if (sizeOptions.size === 0 && packOptions.size === 0 && colorOptions.size === 0) {
        if (product.attributeSize) {
          sizeOptions.add(product.attributeSize);
        }
        if (product.attributePack) {
          // Split pack attribute if it contains multiple values (pipe, comma, or semicolon separated)
          const packValue = product.attributePack;
          if (packValue.includes('|')) {
            packValue.split('|').forEach((p) => packOptions.add(p.trim()));
          } else if (packValue.includes(',')) {
            packValue.split(',').forEach((p) => packOptions.add(p.trim()));
          } else if (packValue.includes(';')) {
            packValue.split(';').forEach((p) => packOptions.add(p.trim()));
          } else {
            packOptions.add(packValue.trim());
          }
        }
        if (product.attributeColor) {
          colorOptions.add(product.attributeColor);
        }
      }

      if (sizeOptions.size > 0) {
        attributes.push({ name: 'Size', options: Array.from(sizeOptions) });
      }
      if (packOptions.size > 0) {
        attributes.push({ name: 'Pack', options: Array.from(packOptions) });
      }
      // Color is optional
      if (colorOptions.size > 0) {
        attributes.push({ name: 'Color', options: Array.from(colorOptions) });
      }
    } else {
      // Variation/Simple products: use option format
      // For variations, inherit size from variable product if not set
      const effectiveSize =
        product.type === 'variation' ? product.attributeSize || inheritedSize : product.attributeSize;
      if (effectiveSize) {
        attributes.push({ name: 'Size', option: effectiveSize });
      }
      if (product.attributePack) {
        attributes.push({ name: 'Pack', option: product.attributePack });
      }
      // Color is optional - inherit from variable product if not set
      const effectiveColor =
        product.type === 'variation' ? product.attributeColor || inheritedColor : product.attributeColor;
      if (effectiveColor) {
        attributes.push({ name: 'Color', option: effectiveColor });
      }
    }

    // Build payload with exact API field names
    const payload: WpProductPayload = {
      ...(existingSku && { sku: existingSku }),
      type: product.type === 'variable' ? 'Variable' : product.type === 'variation' ? 'Variation' : 'Simple',
      ...(product.type === 'variation' ? { status: 'publish' } : existingSku ? {} : { status: 'draft' }),
      stock_status: 'outofstock',
      brand_slug: scAgentEngData?.brandSlug,
      leaf_category_slug: scAgentEngData?.leafCategorySlug,
      short_description: scAgentEngData?.description,
      meta_data: metaData,
      ...(attributes.length > 0 && { attributes }),
    };

    // Add fields based on product type - use scAgentEngData for basic product info
    if (product.type === 'variable') {
      // Variable products: base name without attributes (attributes are in variations)
      const name = scAgentEngData?.name;
      if (name) {
        payload.name = name;
      }
      payload.brand = scAgentEngData?.brand;
      payload.categories = categories;
    }

    if (product.type === 'variation' || product.type === 'simple') {
      // Variations and Simple: include size, color, pack in name
      // For variations, inherit size and color from variable product if not set
      const effectiveSize =
        product.type === 'variation' ? product.attributeSize || inheritedSize : product.attributeSize;
      const effectiveColor =
        product.type === 'variation' ? product.attributeColor || inheritedColor : product.attributeColor;
      payload.name = [scAgentEngData?.name, effectiveSize, effectiveColor, product.attributePack]
        .filter((item) => item !== undefined && item !== '' && item !== null)
        .join(', ');
      payload.brand = scAgentEngData?.brand;
      payload.categories = categories;
    }

    // Add regular_price as top-level Core Field (only for variation and simple, not variable)
    if (product.regularPrice && product.type !== 'variable') {
      payload.regular_price = product.regularPrice;
    }

    return payload;
  };

  const payloads = products.map((product) => transformToWpPayload(product));

  // Track last fetched SKUs to prevent duplicate calls
  const lastFetchedSkusRef = React.useRef<string>('');

  // Fetch product statuses from API when SKUs are available
  useEffect(() => {
    const fetchProductStatuses = async () => {
      // Collect all SKUs
      const skus: string[] = [];
      products.forEach((product, index) => {
        const sku = uploadedSkus.get(index) || (product.cpItemId ? cpiiToSkuMap.get(product.cpItemId) : undefined);
        if (sku) {
          skus.push(sku);
        }
      });

      if (skus.length === 0) {
        return;
      }

      const skuParams = skus.sort().join(',');

      // Skip if we already fetched these exact SKUs
      if (lastFetchedSkusRef.current === skuParams) {
        return;
      }

      try {
        const config = getConfig();
        console.info(`🔍 Fetching product statuses for SKUs: ${skuParams}`);
        const response = await fetch(`${config.apiUrl}/products/admin?skus=${skuParams}`);

        if (response.ok) {
          const productData = await response.json();
          const statusMap = new Map<string, any>();

          if (Array.isArray(productData)) {
            productData.forEach((product: any) => {
              if (product.sku) {
                statusMap.set(product.sku, {
                  status: product.postStatus || 'draft', // initally draft, then user can change to publish once its uploaded.
                  stock_status: product.status || 'instock', // initially outofstock, then user can change to instock once its uploaded.
                });
              }
            });
          }

          setFetchedProductStatuses(statusMap);
          lastFetchedSkusRef.current = skuParams;
        }
      } catch (error) {
        console.error('Failed to fetch product statuses:', error);
      }
    };

    fetchProductStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, uploadedSkus]);

  // Apply edited values to payloads
  const getEditedValue = (rowIndex: number, field: string, originalValue: any): any => {
    const key = `${rowIndex}-${field}`;
    if (editedCells.has(key)) {
      return editedCells.get(key);
    }

    // Fetch status/stock_status from admin API for products with SKUs
    // Show actual WordPress values so users can see and edit them
    if ((field === 'status' || field === 'stock_status') && !editedCells.has(key)) {
      const currentProduct = products[rowIndex];
      const sku =
        uploadedSkus.get(rowIndex) ||
        (currentProduct?.cpItemId ? cpiiToSkuMap.get(currentProduct.cpItemId) : undefined);
      if (sku && fetchedProductStatuses.has(sku)) {
        const fetchedData = fetchedProductStatuses.get(sku);
        return fetchedData[field];
      }
    }

    return originalValue;
  };

  const handleCellEdit = (rowIndex: number, field: string, value: any) => {
    const key = `${rowIndex}-${field}`;
    setEditedCells(new Map(editedCells.set(key, value)));
  };

  const handleUploadToWordPress = async () => {
    const wave = cmWorkflowData?.[0]?.wave;
    if (!wave) {
      alert('Wave is not set in CM workflow! Please set the wave first.');
      return;
    }

    if (!confirm(`Upload ${payloads.length} products to WordPress for Wave ${wave}?`)) {
      return;
    }

    // Apply edited values to payloads before upload
    const finalPayloads = payloads.map((payload, index) => {
      const updatedPayload = { ...payload };

      // Apply edited values and fetched values for editable fields
      const editableFields = ['status', 'stock_status', 'sku', 'regular_price'];
      editableFields.forEach((field) => {
        // Get the effective value (edited or fetched)
        const originalValue =
          field === 'sku'
            ? uploadedSkus.get(index) ||
              (products[index]?.cpItemId ? cpiiToSkuMap.get(products[index].cpItemId) : undefined)
            : payload[field as keyof WpProductPayload];
        const effectiveValue = getEditedValue(index, field, originalValue);

        if (effectiveValue !== undefined && effectiveValue !== null) {
          // Only include SKU if it has a value
          if (field === 'sku' && effectiveValue && String(effectiveValue).trim() !== '') {
            (updatedPayload as any)[field] = String(effectiveValue).trim();
          } else if (field !== 'sku') {
            (updatedPayload as any)[field] = effectiveValue;
          }
        }
      });

      return updatedPayload;
    });

    const batchRequest = {
      products: finalPayloads,
      operator: user?.name || user?.email || 'unknown-user',
      gpi: matchedProduct.gpProductId,
      cppi: matchedProduct.cpProductId,
      wave: wave,
    };

    uploadMutation.mutate(batchRequest, {
      onSuccess: async (response) => {
        // Store SKUs from the response
        if (response?.products && Array.isArray(response.products)) {
          const skuMap = new Map<number, string>();
          response.products.forEach((product: any, index: number) => {
            if (product?.sku) {
              skuMap.set(index, product.sku);
            }
          });
          setUploadedSkus(skuMap);
        }

        // Refetch data to get updated workflow and product information
        if (refetchCMWorkflow) {
          await refetchCMWorkflow();
        }
        if (refetchMatchedProduct) {
          await refetchMatchedProduct();
        }
        if (refetchProductDetails) {
          await refetchProductDetails(true); // Force refresh to show new data
        }

        const totalCreated = response?.totalCreated || payloads.length;
        alert(
          `Successfully uploaded ${totalCreated} products to WordPress (Wave ${wave})!\n` +
            `Workflow updated: ${response?.workflowUpdated ? 'Yes' : 'No'}`,
        );
      },
      onError: (error) => {
        console.error('Upload error:', error);
        alert('Failed to upload products. Please check the console for details.');
      },
    });
  };

  // Core fields to display (exact API field names) - Editable fields first
  const coreFields = ['sku', 'status', 'stock_status', 'regular_price', 'type', 'name', 'brand', 'categories'];

  // Attribute fields (separate columns)
  const attributeFields = ['Size', 'Pack', 'Color'];

  // Ops data fields
  const opsFields = ['brand_slug', 'leaf_category_slug'];

  // Meta data keys (collect all unique keys from meta_data arrays)
  const allMetaKeys = new Set<string>();
  payloads.forEach((payload) => {
    payload.meta_data.forEach((meta) => allMetaKeys.add(meta.key));
  });
  const metaKeys = Array.from(allMetaKeys);

  const renderCellValue = (value: any): string => {
    if (Array.isArray(value)) {
      return value.join(', ') || '-';
    }
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value);
    }
    if (typeof value === 'boolean') {
      return String(value);
    }
    if (value === undefined || value === null || value === '') {
      return '-';
    }
    return String(value);
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Upload Loading Overlay */}
      {uploadMutation.isPending && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
          }}
        >
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="text-primary mt-3 mb-0" style={{ fontSize: '1.1rem', fontWeight: 500 }}>
            Uploading products to WordPress...
          </p>
          <p className="text-muted mt-2" style={{ fontSize: '0.9rem' }}>
            Please wait while we upload {products.length} product{products.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}

      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h6 className="text-muted mb-1">WordPress Upload Payload Preview</h6>
            <p className="text-muted small mb-0">
              Spreadsheet view of all fields and metadata to be sent to WooCommerce
            </p>
          </div>
          <div className="d-flex gap-2">
            <Badge bg="primary">Variable: {products.filter((p) => p.type === 'variable').length}</Badge>
            <Badge bg="warning">Variation: {products.filter((p) => p.type === 'variation').length}</Badge>
            <Badge bg="success">Simple: {products.filter((p) => p.type === 'simple').length}</Badge>
          </div>
        </div>
      </div>

      {products.length === 0 ? (
        <div className="text-center py-5">
          <Package size={48} className="text-muted mb-3" />
          <h6 className="text-muted">No Products to Upload</h6>
          <p className="text-muted small mb-0">
            Complete the Product Restructure step to generate products for upload.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-3 d-flex gap-2 align-items-center justify-content-between">
            <div className="d-flex gap-2 align-items-center">
              <Button
                variant="success"
                size="sm"
                onClick={handleUploadToWordPress}
                disabled={uploadMutation.isPending || products.length === 0}
              >
                {uploadMutation.isPending ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-1" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={16} className="me-1" />
                    Upload to WordPress
                  </>
                )}
              </Button>
            </div>

            <Form.Check
              type="checkbox"
              id="show-all-fields-checkbox"
              label="Show all fields"
              checked={showAllFields}
              onChange={(e) => setShowAllFields(e.target.checked)}
            />
          </div>

          <div className="alert alert-info py-2 mb-3" style={{ fontSize: '0.8rem' }}>
            <strong>ℹ️ Tips:</strong>
            <ul className="mb-0 mt-1" style={{ paddingLeft: '1.2rem' }}>
              <li>
                <strong>SKU Handling:</strong> You can edit SKU values before uploading. Existing SKUs from CM Workflow
                are pre-filled. Leave empty to auto-generate new SKUs by WordPress.
              </li>
              <li>
                <strong>Status Defaults:</strong> Variations default to <strong>publish</strong>, Variables and Simple
                products default to <strong>draft</strong>. You can change these values before uploading.
              </li>
            </ul>
          </div>

          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '600px' }}>
            <Table bordered hover size="sm" style={{ fontSize: '0.75rem', minWidth: '1200px' }}>
              <thead className="bg-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                <tr>
                  <th style={{ minWidth: '40px', position: 'sticky', left: 0, backgroundColor: '#f8f9fa', zIndex: 11 }}>
                    #
                  </th>
                  <th style={{ minWidth: '80px' }} className="text-center">
                    Action
                  </th>

                  {/* Core Fields Section */}
                  <th colSpan={coreFields.length} className="text-center bg-primary text-white">
                    Core Fields
                  </th>

                  {/* Attributes Section */}
                  <th colSpan={attributeFields.length} className="text-center bg-success text-white">
                    Attributes
                  </th>

                  {/* Ops Data Section */}
                  <th colSpan={opsFields.length} className="text-center bg-info text-white">
                    Ops Data
                  </th>

                  {/* Meta Data Section */}
                  {showAllFields && (
                    <th colSpan={metaKeys.length} className="text-center bg-warning text-dark">
                      Meta Data ({metaKeys.length} fields)
                    </th>
                  )}
                </tr>
                <tr>
                  <th style={{ position: 'sticky', left: 0, backgroundColor: '#f8f9fa', zIndex: 11 }}>#</th>
                  <th className="text-center">JSON</th>

                  {/* Core field headers */}
                  {coreFields.map((field) => (
                    <th key={field} style={{ minWidth: '120px' }}>
                      <code className="text-primary" style={{ fontSize: '0.7rem' }}>
                        {field}
                      </code>
                    </th>
                  ))}

                  {/* Attribute headers - separate columns */}
                  {attributeFields.map((field) => (
                    <th key={field} style={{ minWidth: '120px' }}>
                      <code className="text-success" style={{ fontSize: '0.7rem' }}>
                        {field}
                      </code>
                    </th>
                  ))}

                  {/* Ops data headers */}
                  {opsFields.map((field) => (
                    <th key={field} style={{ minWidth: '120px' }}>
                      <code className="text-info" style={{ fontSize: '0.7rem' }}>
                        {field}
                      </code>
                    </th>
                  ))}

                  {/* Meta data headers */}
                  {showAllFields &&
                    metaKeys.map((key) => (
                      <th key={key} style={{ minWidth: '120px' }}>
                        <code className="text-warning" style={{ fontSize: '0.7rem' }}>
                          {key}
                        </code>
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {payloads.map((payload, index) => (
                  <React.Fragment key={index}>
                    <tr>
                      <td
                        style={{ position: 'sticky', left: 0, backgroundColor: '#fff', fontWeight: 'bold', zIndex: 9 }}
                      >
                        {index + 1}
                      </td>

                      {/* JSON Preview Button */}
                      <td className="text-center">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setShowJsonPreview(showJsonPreview === index ? null : index)}
                          style={{ fontSize: '0.65rem', padding: '2px 6px' }}
                        >
                          {showJsonPreview === index ? <ChevronDown size={12} /> : <Code size={12} />}
                        </Button>
                      </td>

                      {/* Core field values */}
                      {coreFields.map((field) => {
                        // Hide regular_price for Variable products (they don't have independent pricing)
                        if (field === 'regular_price' && payload.type === 'Variable') {
                          return (
                            <td key={field} style={{ backgroundColor: '#f8f9fa' }}>
                              -
                            </td>
                          );
                        }

                        // Special handling for SKU field - get from uploadedSkus map or CM Workflow mappings
                        let originalValue;
                        if (field === 'sku') {
                          // First check uploadedSkus (from recent upload), then check CM Workflow mappings
                          const currentProduct = products[index];
                          originalValue =
                            uploadedSkus.get(index) ||
                            (currentProduct?.cpItemId ? cpiiToSkuMap.get(currentProduct.cpItemId) : undefined) ||
                            undefined;
                        } else {
                          originalValue = payload[field as keyof WpProductPayload];
                        }
                        const value = getEditedValue(index, field, originalValue);
                        const cellKey = `${index}-${field}`;
                        const isEdited = editedCells.has(cellKey);

                        // Fields that should be editable
                        const editableFields = ['status', 'stock_status', 'sku', 'regular_price'];
                        const isEditable = editableFields.includes(field);

                        // Fields that are read-only
                        const readOnlyFields = ['name', 'brand', 'categories', 'type'];
                        const isReadOnly = readOnlyFields.includes(field);

                        return (
                          <td
                            key={field}
                            title={
                              field === 'sku'
                                ? value
                                  ? `SKU: ${value} (editable)`
                                  : 'SKU will be auto-generated if left empty'
                                : isReadOnly
                                  ? 'Read-only field'
                                  : renderCellValue(value)
                            }
                            style={{
                              backgroundColor: isReadOnly
                                ? '#f8f9fa'
                                : isEditable
                                  ? '#fffbf0'
                                  : isEdited
                                    ? '#fff3cd'
                                    : 'transparent',
                            }}
                          >
                            {field === 'sku' ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Form.Control
                                  type="text"
                                  value={value || ''}
                                  onChange={(e) => handleCellEdit(index, field, e.target.value)}
                                  placeholder="Auto-generated"
                                  size="sm"
                                  style={{
                                    fontSize: '0.75rem',
                                    padding: '2px 4px',
                                    fontFamily: 'monospace',
                                    flex: 1,
                                  }}
                                />
                                {value && (
                                  <a
                                    href={`${getConfig().adminUrl}/catalog/ss-editor?sku=${value}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Open in SS Editor"
                                    style={{ color: '#0d6efd', display: 'flex' }}
                                  >
                                    <ExternalLink size={12} />
                                  </a>
                                )}
                              </div>
                            ) : field === 'type' ? (
                              <StatusBadge type="productType" value={value?.toLowerCase() || 'simple'} size="sm" />
                            ) : field === 'status' ? (
                              <Form.Select
                                value={value || 'draft'}
                                onChange={(e) => handleCellEdit(index, field, e.target.value)}
                                size="sm"
                                style={{ fontSize: '0.75rem', padding: '2px 4px' }}
                              >
                                <option value="draft">Draft</option>
                                <option value="pending">Pending</option>
                                <option value="private">Private</option>
                                <option value="publish">Publish</option>
                              </Form.Select>
                            ) : field === 'stock_status' ? (
                              <Form.Select
                                value={value || 'instock'}
                                onChange={(e) => handleCellEdit(index, field, e.target.value)}
                                size="sm"
                                style={{ fontSize: '0.75rem', padding: '2px 4px' }}
                              >
                                <option value="instock">In Stock</option>
                                <option value="outofstock">Out of Stock</option>
                                <option value="onbackorder">On Backorder</option>
                              </Form.Select>
                            ) : field === 'regular_price' ? (
                              <Form.Control
                                type="text"
                                value={value || ''}
                                onChange={(e) => handleCellEdit(index, field, e.target.value)}
                                placeholder="0.00"
                                size="sm"
                                style={{
                                  fontSize: '0.75rem',
                                  padding: '2px 4px',
                                }}
                              />
                            ) : isReadOnly ? (
                              <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>{renderCellValue(value)}</span>
                            ) : (
                              renderCellValue(value)
                            )}
                          </td>
                        );
                      })}

                      {/* Attribute values - separate columns (Read-only: Fixed in Product Restructure step) */}
                      {attributeFields.map((attrName) => {
                        const attr = payload.attributes?.find((a) => a.name === attrName);
                        // Use pipe separator for Pack, comma for others
                        const separator = attrName === 'Pack' ? ' | ' : ', ';
                        const displayValue = attr
                          ? 'options' in attr
                            ? attr.options.join(separator)
                            : attr.option || ''
                          : '';

                        return (
                          <td
                            key={attrName}
                            style={{
                              minWidth: 120,
                              backgroundColor: '#f8f9fa',
                            }}
                            title="Attributes are read-only. Please edit them in Product Restructure step."
                          >
                            <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>{displayValue || '-'}</span>
                          </td>
                        );
                      })}

                      {/* Ops data values (Read-only) */}
                      {opsFields.map((field) => {
                        const originalValue = payload[field as keyof WpProductPayload];
                        const value = getEditedValue(index, field, originalValue);

                        return (
                          <td key={field} title={renderCellValue(value)} style={{ backgroundColor: '#f8f9fa' }}>
                            <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>{renderCellValue(value)}</span>
                          </td>
                        );
                      })}

                      {/* Meta data values (Read-only) */}
                      {showAllFields &&
                        metaKeys.map((key) => {
                          const metaItem = payload.meta_data.find((m) => m.key === key);
                          const value = metaItem?.value || '';

                          return (
                            <td key={key} title={renderCellValue(value)} style={{ backgroundColor: '#f8f9fa' }}>
                              <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>{value || '-'}</span>
                            </td>
                          );
                        })}
                    </tr>

                    {/* JSON Preview Row */}
                    {showJsonPreview === index && (
                      <tr>
                        <td colSpan={coreFields.length + metaKeys.length + 3} className="p-0">
                          <Collapse in={showJsonPreview === index}>
                            <div className="p-3 bg-light">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="mb-0">
                                  <Code size={16} className="me-2" />
                                  API Request JSON
                                </h6>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => {
                                    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
                                    alert('JSON copied to clipboard!');
                                  }}
                                  style={{ fontSize: '0.7rem' }}
                                >
                                  Copy JSON
                                </Button>
                              </div>
                              <pre
                                className="mb-0 p-3 bg-dark text-light rounded"
                                style={{
                                  fontSize: '0.75rem',
                                  maxHeight: '400px',
                                  overflowY: 'auto',
                                }}
                              >
                                {JSON.stringify(payload, null, 2)}
                              </pre>
                            </div>
                          </Collapse>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
};
